/* eslint-env browser */
import React, { Component, ComponentPropsWithoutRef } from 'react'
import { Masthead } from './pureMasthead'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { Helmet } from 'react-helmet'
import { Paywall as TamediaPaywall } from '@ta-interaktiv/react-paywall'
import { Tracking, Props as TrackingProps } from '@ta-interaktiv/react-tracking'
import {
  ShareButtonsProps,
  defaultClickHandler
} from '@ta-interaktiv/react-share-buttons'
import { ArticleId, Locale, Paywall } from '@ta-interaktiv/types'
import {
  getNiceName,
  getFacebookAppId,
  getTitle,
  getLead,
  getLanguage,
  getDocType,
  getCategory,
  getSubCategory,
  getGtmId
} from '@ta-interaktiv/api-access'

interface Props
  extends Partial<ComponentPropsWithoutRef<typeof Masthead>>,
    ArticleId,
    Locale {
  /** Whether the paywall should
   * be enabled. */
  enablePaywall?: boolean

  /** The ID of
   * the DOM element that should contain the paywall elements. */
  paywallContainerId?: string

  /**
   * Additional shares that got or will be lost due to the transfer to a new
   * Share Counts API
   */
  additionalShares?: number

  /**
   * Previously used prop for missing Facebook shares. Use the more general
   * @link{additionalShares} instead.
   *
   * @deprecated
   */
  additionalFacebookShares?: number
}

type DynamicButtonProps = Pick<
  ShareButtonsProps,
  'clickHandler' | 'facebookAppId' | 'twitterHandle' | 'lead' | 'title'
>
type DynamicTrackingProps = Partial<Pick<TrackingProps, 'gtmId'>>

interface State
  extends DynamicButtonProps,
    DynamicTrackingProps,
    Partial<Paywall>,
    Locale {
  fullMediaName?: string
  homepage: string
  mediaName: string
  url?: string
  title: string
}

/**
 * Display the masthead in standalone projects. Adapts to the publication, using the
 * domain name as a hint. This can be overwritten, using the same properties as
 * the pure version of the masthead uses.
 *
 * This component also acts as a meta-component, additionally integrating the
 * **Paywall** and **Tracking** components. So if you have this masthead
 * in your standalone, you won't have to worry about the paywall or the
 * tracking anymore.
 *
 * ## Installation
 *
 * ```bash
 * yarn add @ta-interaktiv/react-masthead
 * ```
 *
 * ## Usage
 * ```jsx
 * import React from 'react'
 * import { Masthead } from '@ta-interaktiv/react-masthead'
 *
 * export const Component() {
 *   return (
 *     <Masthead
 *        articleId='00000000'
 *     />
 *   )
 *
 * }
 * ```
 */
export class PolymorphicMasthead extends Component<Props, State> {
  private parsedHostname = parseHostname()
  private readonly articleId: string = this.props.articleId

  public static defaultProps = {
    enablePaywall: true,
    defaultLanguage: 'de',
    paywallContainerId: 'paywall_container'
  }

  public state: State = {
    locale: this.props.locale,
    clickHandler: (shareType, event) => {
      this.props.clickHandler && this.props.clickHandler(shareType, event)
      defaultClickHandler(
        this.articleId,
        shareType,
        this.parsedHostname.publicationName,
        this.props.url
      )
    },
    homepage:
      this.props.homepage || `//www.${this.parsedHostname.publicationDomain}`,
    mediaName: this.props.mediaName || this.parsedHostname.publicationName,
    fullMediaName: this.props.fullMediaName || '',
    title: window.document.title.toString()
  }

  errorHandler = (e: Error) => {
    console.warn(
      '[Masthead] Could not load data for article ID ' + this.props.articleId,
      e
    )
  }

  public render() {
    const paywallIsReady =
      this.state.doc_type && this.state.main_channel && this.state.sub_channel

    return (
      <Masthead {...this.props} {...this.state}>
        <>
          <Helmet
            titleTemplate={`%s ${
              this.state.fullMediaName ? '| ' + this.state.fullMediaName : ''
            }`}
          >
            <title>{this.state.title}</title>
          </Helmet>
          {this.props.enablePaywall && paywallIsReady && (
            <TamediaPaywall
              isEnabled={this.props.enablePaywall}
              paywallContainerId={this.props.paywallContainerId}
              paywallConfiguration={{
                cms_id: parseInt(this.props.articleId),
                doc_type: this.state.doc_type || 'article',
                main_channel: this.state.main_channel as string,
                sub_channel: this.state.sub_channel as string
              }}
            />
          )}
          {this.state.gtmId && (
            <Tracking
              articleId={this.props.articleId}
              gtmId={this.state.gtmId}
            />
          )}
          {this.props.children}
        </>
      </Masthead>
    )
  }

  public componentDidMount() {
    getLanguage(this.parsedHostname.publicationName).then(lang => {
      this.setState({ locale: lang })
    })
    getNiceName(this.parsedHostname.publicationName).then(name => {
      this.setState({ fullMediaName: this.props.fullMediaName || name })
    })
    getFacebookAppId(this.parsedHostname.publicationName).then(id => {
      this.setState({ facebookAppId: id })
    })

    getTitle(this.parsedHostname.publicationName, this.props.articleId)
      .then(title => {
        this.setState({ title: title || window.document.title })
      })
      .catch(this.errorHandler)

    getLead(this.parsedHostname.publicationName, this.props.articleId)
      .then(lead => {
        this.setState({ lead: lead })
      })
      .catch(this.errorHandler)

    // Get Paywall configuration
    getDocType(this.parsedHostname.publicationName, this.props.articleId)
      .then(docType => {
        this.setState({ doc_type: docType })
      })
      .catch(this.errorHandler)

    getCategory(this.parsedHostname.publicationName, this.props.articleId)
      .then(category => {
        this.setState({ main_channel: category })
      })
      .catch(this.errorHandler)

    getSubCategory(this.parsedHostname.publicationName, this.props.articleId)
      .then(subCategory => {
        this.setState({ sub_channel: subCategory })
      })
      .catch(this.errorHandler)

    // Get Tracking configuration
    getGtmId(this.parsedHostname.publicationName).then(id => {
      this.setState({ gtmId: id ?? undefined })
    })
  }
}
